import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FilterItem, FilterItemTypes } from "configs/types";

import InnerInput from "../../inputs/InnerInput/InnerInput";
import SearchInput from "../../inputs/SearchInput/SearchInput";
import Button from "../../buttons/Button";
import FilterMultiselect from "../../filterItems/FilterMultiselect";
import FilterDatePicker from "../../filterItems/FilterDatePicker";
import FilterChipInput from "../../filterItems/FilterChipInput";
import FilterTabs from "../../FilterTabs";

import toggleIcon from "assets/icons/sidebar/toggle.svg";
import filterIcon from "assets/icons/filter-icon.svg";
import arrowIcon from "assets/icons/arrow-down.svg";
import { useLocation } from "react-router-dom";
import cn from 'clsx';

import "./Filter.scss";

interface Props {
  className?: string;
  activeOptions?: string[];
  disabled?: boolean;
  applyDisabled?: boolean;
  search?: string;
  placeholder?: string;
  searchLabel?: string;
  radioLabelsInFlow?: boolean;
  options?: {
    value: string;
    label: string;
  }[];
  onSelectOption?: (e: string) => void;
  onSearch?: (e: string) => void;
  filterItems?: FilterItem[];
  handleApply: () => void;
  handleClear: () => void;
  onClose?: () => void;
}

const Filter: React.FC<Props> = ({
  className,
  activeOptions,
  options,
  onSelectOption,
  search,
  onSearch,
  placeholder,
  searchLabel,
  radioLabelsInFlow,
  filterItems,
  handleApply,
  handleClear,
  disabled,
  applyDisabled,
}) => {
  const { t } = useTranslation();

  const [menuOpened, setMenuOpened] = useState<boolean>(false);
  const [activeFilterTitle, setActiveFilterTitle] = useState<string>("");
  const activeItemRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    handleClear();
  }, [location.pathname]);



  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;

      if (!menuOpened) return;

      e.preventDefault();
      handleApply();

      toggleMenu();
      setActiveFilterTitle("");
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpened, handleApply]);


    const toggleMenu = () => {
      if (disabled) return;

      setMenuOpened((prev) => {
        const next = !prev;
        if (!next) {
          setActiveFilterTitle("");
        }
        return next;
      });
    };

  const renderFormItem = (item: FilterItem) => {
    if (item.type === FilterItemTypes.input && item.setInputValue) {
      if (item.setSecondInputValue) {
        return (
          <div className="filter-input-row">
            <InnerInput
              id={`${item.title}-from-filter`}
              placeholder={item.boxTitle}
              label={item.boxTitle}
              type={item.inputType ?? "text"}
              value={item.inputValue ?? ""}
              onChange={(e) => {
                if (item.setInputValue) {
                  item.setInputValue(e);
                }
              }}
            />
            <InnerInput
              id={`${item.title}-to-filter`}
              placeholder={item.secondBoxTitle ?? ""}
              label={item.secondBoxTitle ?? ""}
              type={item.inputType ?? "text"}
              value={item.secondInputValue ?? ""}
              onChange={(e) => {
                if (item.setSecondInputValue) {
                  item.setSecondInputValue(e);
                }
              }}
            />
          </div>
        );
      }

      return (
        <InnerInput
          id={`${item.title}-filter`}
          placeholder={item.inputPlaceholder ?? item.boxTitle}
          label={item.boxTitle}
          type={item.inputType ?? "text"}
          value={item.inputValue ?? ""}
          onChange={(e) => {
            if (item.setInputValue) {
              item.setInputValue(e);
            }
          }}
        />
      );
    }
    if (
      item.type === FilterItemTypes.select &&
      item.itemsArray &&
      item.activeItems &&
      item.handleApply
    ) {
      return (
        <FilterMultiselect
          anchorRef={activeItemRef}
          placeholder={t("searchYourEntity")}
          isVisible={activeFilterTitle === item.title}
          title={item.boxTitle}
          options={item.itemsArray.filter(
            (el) => !item.activeItems?.includes(el)
          )}
          onApply={item.handleApply}
          onClose={() => setActiveFilterTitle("")}
          isSingle
        />
      );
    }
    if (
      item.type === FilterItemTypes.radio &&
      item.itemsArray &&
      item.activeItems &&
      item.handleApply
    ) {
      return (
        <div className="filter-radio-wrap">
          {radioLabelsInFlow && (
            <div className="filter-radio-label">{item.title}</div>
          )}
          <FilterTabs
            value={item.activeItems[0]?.value}
            isInner
            options={item.itemsArray}
            label={radioLabelsInFlow ? undefined : item.title}
            onSelect={(e) => {
              if (item?.handleApply)
                item?.handleApply(
                  item.itemsArray?.filter((el) => el.value === e) ?? []
                );
            }}
          />
        </div>
      );
    }
    if (
      item.type === FilterItemTypes.multiselect &&
      item.itemsArray &&
      item.activeItems &&
      item.handleApply
    ) {
      return (
        <FilterMultiselect
          anchorRef={activeItemRef}
          placeholder={t("searchYourEntity")}
          isVisible={activeFilterTitle === item.title}
          title={item.boxTitle}
          options={item.itemsArray.filter(
            (el) => !item.activeItems?.includes(el)
          )}
          onApply={item.handleApply}
          onClose={() => setActiveFilterTitle("")}
        />
      );
    }
    if (item.type === FilterItemTypes.datePicker && item?.handleApplyDate) {
      return (
        <FilterDatePicker
          anchorRef={activeItemRef}
          firstDatePlaceholder={item?.firstDatePlaceholder ?? ""}
          secondDatePlaceholder={item?.secondDatePlaceholder ?? ""}
          isVisible={activeFilterTitle === item.title}
          title={item.boxTitle}
          onApply={item?.handleApplyDate}
          onClose={() => setActiveFilterTitle("")}
          firstDateInitValue={item.firstDateInitValue}
          secondDateInitValue={item.secondDateInitValue}
        />
      );
    }
    if (
      item.type === FilterItemTypes.chipInput &&
      item.activeItems &&
      item.handleApply
    ) {
      return (
        <FilterChipInput
          anchorRef={activeItemRef}
          isVisible={activeFilterTitle === item.title}
          title={item.boxTitle}
          placeholder={item.boxTitle}
          onApply={item.handleApply}
          onClose={() => setActiveFilterTitle("")}
        />
      );
    }
    return "";
  };

  const renderItems = (items: FilterItem[]) => {
    const result = items.map((el) => (
      <div
        id={`${el.title}-filter`}
        key={el.title}
        ref={activeFilterTitle === el.title ? activeItemRef : null}
        className={`filter-item filter-item--${el.type} ${activeFilterTitle === el.title ? "active" : ""}`}
      >
        {el.type !== FilterItemTypes.radio &&
          el.type !== FilterItemTypes.input && (
            <>
              <div
                className="filter-item-content"
                onClick={() => {
                  if (activeFilterTitle === el.title) {
                    setActiveFilterTitle("");
                  } else {
                    setActiveFilterTitle(el.title);
                  }
                }}
              >
                <div className="left">
                  <img src={el.icon} alt="" />
                  <div className="filter-title">{el.title}</div>
                </div>
                <img className="arrow" src={arrowIcon} alt="" />
              </div>
              <div
                className={`active-items ${el.activeItems && el.activeItems.length > 0 ? "bordered" : ""}`}
              >
                {el.activeItems?.map((activeItem) => (
                  <div className="activ-item" key={activeItem.value}>
                    <span>{activeItem.label}</span>
                    <div
                      onClick={() => {
                        if (el.onDeleteItem) {
                          el.onDeleteItem(activeItem.value as string);
                          return;
                        }
                        if (el.handleApply && el.activeItems) {
                          el.handleApply(
                            el.activeItems.filter(
                              (fItem) => fItem.value !== activeItem.value
                            ),
                            true
                          );
                        }
                      }}
                      className="close"
                    >
                      X
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        {renderFormItem(el)}
        {el.type === FilterItemTypes.input &&
          el.activeItems &&
          el.activeItems.length > 0 && (
            <div className="active-items bordered">
              {el.activeItems.map((activeItem) => (
                <div className="activ-item" key={activeItem.value}>
                  <span>{activeItem.label}</span>
                  <div
                    onClick={() => {
                      if (el.onDeleteItem) {
                        el.onDeleteItem(String(activeItem.value));
                      }
                    }}
                    className="close"
                  >
                    X
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    ));

    return result;
  };

  return (
    <div
      className={cn(
        'filter-wrapper',
        className,
        !menuOpened && 'closed',
        activeFilterTitle && 'item-acivated',
        disabled && 'disabled'
      )}
    >
      <div className="filter-header" onClick={toggleMenu}>
        <img className="filter-icon" src={filterIcon} alt="" />
        <div className="toggle">
          <img src={toggleIcon} alt="" />
        </div>
      </div>
      <div
        className="filter-content"
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;

          if (e.key === "Enter" && menuOpened) {
            e.preventDefault();
            handleApply();
          }

          e.preventDefault();
          handleApply();

          if (menuOpened) {
            toggleMenu();
            setActiveFilterTitle("");
          }
        }}
      >
        {search !== undefined && onSearch && placeholder && (
          <div className="filter-search">
            {searchLabel && <div className="filter-search__label">{searchLabel}</div>}
            <SearchInput
              placeholder={placeholder}
              value={search}
              onChange={onSearch}
              showIcon={false}
            />
          </div>
        )}
        {filterItems && <>{renderItems(filterItems)}</>}
        {options && activeOptions && onSelectOption && (
          <div className="filter-options">
            {options.map((el) => (
              <div
                className={`option ${activeOptions.includes(el.value) ? "active" : ""}`}
                key={el.value}
                onClick={() => {
                  onSelectOption(el.value);
                }}
              >
                {el.label}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="filter-btns">
        <Button
          handleClick={() => {
            handleClear();
          }}
          isBordered
          isSmall
          isSecondary
          txt={t("clearFilter")}
        />
        <Button
          handleClick={() => {
            handleApply();
            toggleMenu();
            setActiveFilterTitle("");
          }}
          isSmall
          isGreen
          disabled={applyDisabled}
          txt={t("applyFilter")}
        />
      </div>
    </div>
  );
};

export default Filter;
